import { ModelFeedbackResultCodeToLabel } from 'Constants/customTask.constants'
import { MetaSubmitGenerateResponseDataPayload } from 'HTTPClients/customTask'
import { forEach } from 'lodash'
import {
    ModelFeedback,
    StreamModelResponseOptions,
    TransformedModelFeedback,
} from 'Types/CustomTask.types'

export const getTransformedModelFeedback = (
    feedbacks: ModelFeedback[]
): TransformedModelFeedback[] => {
    let additionalFeedbackCount = 1
    const items: TransformedModelFeedback[] = []
    forEach(feedbacks, (feedback: ModelFeedback) => {
        let label = ModelFeedbackResultCodeToLabel[feedback.feedback_result_code]
        if (!label) {
            label = `Additional Feedback ${additionalFeedbackCount}`
            additionalFeedbackCount += 1
        }
        items.push({
            ...feedback,
            feedback_result_code_label: label,
        })
    })
    return items
}

export const streamModelResponse = async ({
    reader,
    onModelMessage,
    onModelFeedback,
    onFinalResponse,
    onError,
    onComplete,
    signal,
}: StreamModelResponseOptions) => {
    if (!reader) {
        onError?.('Error in fetching model response. Please try again.')
        onComplete?.(true)
        return
    }

    const decoder = new TextDecoder()
    let buffer = ''
    let done = false
    let hasError = false

    const abortHandler = () => {
        reader.cancel().catch(() => {})
        onComplete?.(true)
    }

    signal?.addEventListener('abort', abortHandler)

    try {
        while (!done) {
            if (signal?.aborted) break

            const { value, done: streamDone } = await reader.read()
            done = streamDone

            const chunk = decoder.decode(value || new Uint8Array(), { stream: true })
            buffer += chunk

            if (!done) {
                // Process all complete JSON objects in the buffer
                // We need to find JSON object boundaries by counting braces since
                // the JSON content itself may contain newlines
                while (buffer.trim()) {
                    let braceCount = 0
                    let inString = false
                    let escapeNext = false
                    let jsonEndIndex = -1
                    let jsonStartIndex = -1

                    // Find the start and end of the first complete JSON object
                    for (let i = 0; i < buffer.length; i++) {
                        const char = buffer[i]

                        if (escapeNext) {
                            escapeNext = false
                            continue
                        }

                        if (char === '\\' && inString) {
                            escapeNext = true
                            continue
                        }

                        if (char === '"') {
                            inString = !inString
                            continue
                        }

                        if (!inString) {
                            if (char === '{') {
                                if (braceCount === 0) {
                                    jsonStartIndex = i
                                }
                                braceCount++
                            } else if (char === '}') {
                                braceCount--
                                if (braceCount === 0 && jsonStartIndex >= 0) {
                                    jsonEndIndex = i + 1
                                    break
                                }
                            }
                        }
                    }

                    // If we found a complete JSON object, parse it
                    if (jsonEndIndex > 0 && jsonStartIndex >= 0) {
                        const jsonStr = buffer.substring(jsonStartIndex, jsonEndIndex)
                        buffer = buffer.substring(jsonEndIndex).trimStart()

                        try {
                            const partial = JSON.parse(jsonStr)

                            if ('message' in partial) {
                                onModelMessage?.(partial.message)
                            } else if (partial?.submission) {
                                const submission =
                                    partial.submission as MetaSubmitGenerateResponseDataPayload['submission']
                                const promptRevisions =
                                    submission.output_container.data.data.response.main_info.value
                                        .prompt_revisions

                                const feedback = getTransformedModelFeedback(
                                    promptRevisions?.[promptRevisions.length - 1]
                                        .feedback_from_model ?? []
                                )
                                const modelResponse =
                                    promptRevisions?.[promptRevisions.length - 1]
                                        .steps_annotation ?? ''

                                onModelFeedback?.(feedback)
                                onFinalResponse?.(feedback, submission, modelResponse)
                            }
                        } catch (e) {
                            console.error('Failed to parse JSON:', jsonStr, e)
                        }
                    } else {
                        // No complete JSON object found, wait for more data
                        console.log('Incomplete JSON, waiting for more data...')
                        break
                    }
                }
            } else {
                if (buffer) {
                    hasError = true
                    onError?.('Invalid model response format.')
                }
                onComplete?.(hasError)
            }
        }
    } catch (err) {
        hasError = true
        onError?.((err as Error).message || 'Unexpected error while streaming.')
        onComplete?.(true)
    } finally {
        signal?.removeEventListener('abort', abortHandler)
    }
}
